/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { ComponentProps, useCallback, useEffect, useState } from 'react'

import { pxToRem } from '@/shared/styles/themes'
import { styled } from '@mui/material'

import { useMegaMenuContext } from './MegaMenuContext'
import { StyledTab } from './styledTab'
import { StyledTabPanel } from './styledTabPanel'

export type MegaMenuTabsProps = Pick<ComponentProps<typeof StyledMegaMenuContainer>, 'sx'> & {
  children?: React.ReactNode
  idx: number
}

export const MegaMenuTabs: React.FC<MegaMenuTabsProps> = ({ idx, children, ...props }) => {
  const { menuState } = useMegaMenuContext()
  const [hasSticky, setHasNavSticky] = useState(false)

  // Change absolute position of the Megamenu Dropdown when scroll
  const handleNavigation = useCallback(
    (e: any) => {
      const window = e.currentTarget

      if (window) {
        if (!hasSticky && window.scrollY >= 116) {
          setHasNavSticky(true)
        } else if (hasSticky && window.scrollY < 116) {
          setHasNavSticky(false)
        }
      }
    },
    [hasSticky],
  )

  useEffect(() => {
    if (window) {
      window.addEventListener('scroll', handleNavigation)
    }

    return () => {
      if (window) {
        window.removeEventListener('scroll', handleNavigation)
      }
    }
  }, [handleNavigation])

  return (
    <StyledMegaMenuContainer $show={idx === menuState.tabsActive} $hasSticky={hasSticky} {...props}>
      {children}
    </StyledMegaMenuContainer>
  )
}

const StyledMegaMenuContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== '$show' && prop !== '$hasSticky',
}) <{ $show: boolean; $hasSticky: boolean }>`
  position: absolute;
  display: flex;
  width: calc(100vw - ${pxToRem(16)});
  max-width: ${pxToRem(1400)};
  margin: auto;
  justify-self: center;
  justify-content: center;
  visibility: ${(props) => (props.$show ? 'visible' : 'hidden')};
  background-color: ${(props) => props.theme.palette.background.default};
  top: ${pxToRem(38)};
  z-index: ${(props) => props.theme.zIndex.appBar + 1};
  -webkit-transform: translate3d(0, 0, 0);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
  border-radius: 0 0 ${pxToRem(16)} ${pxToRem(16)};
  will-change: opacity;
  border: 1px solid ${(props) => props.theme.palette.divider};
  left: ${pxToRem(8)};
  right: ${pxToRem(8)};

  ${(props) => props.theme.breakpoints.up('lg')} {
    left: 0;
    right: 0;
  }

  &:hover {
    visibility: visible;
  }

  ${StyledTab}:hover {
    ${StyledTabPanel} {
      visibility: visible;
    }
  }
`
